import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';

const NotificationContext = createContext(null);

function getWsUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api' : '/api');
  // Handle relative URLs (production behind Nginx)
  if (apiUrl.startsWith('/')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/notifications/`;
  }
  const base = apiUrl.replace(/\/+$/, '').replace(/\/api$/, '');
  const wsBase = base.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  return `${wsBase}/ws/notifications/`;
}

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const pollingTimerRef = useRef(null);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const data = await notificationService.unreadCount();
      setUnreadCount(data.unread_count || 0);
      return data;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        setUnreadCount(0);
        return { unread_count: 0 };
      }
      throw error;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await notificationService.list();
      const results = data?.results ?? data;
      setItems(Array.isArray(results) ? results : []);
      await refreshUnreadCount();
      return data;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        setItems([]);
        setUnreadCount(0);
        return { results: [] };
      }
      throw error;
    }
  }, [refreshUnreadCount]);

  const markRead = useCallback(async (id) => {
    await notificationService.markRead(id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    await refreshUnreadCount();
  }, [refreshUnreadCount]);

  const markAllRead = useCallback(async () => {
    await notificationService.markAllRead();
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setUnreadCount(0);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      reconnectAttemptRef.current = 0;
      return;
    }

    refresh();

    const startPolling = () => {
      if (pollingTimerRef.current) return;
      pollingTimerRef.current = setInterval(() => {
        refreshUnreadCount();
      }, 15000);
    };

    const stopPolling = () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };

    const connectWs = () => {
      if (!isAuthenticated) return;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        stopPolling();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg?.event === 'notification.created' && msg.payload) {
            setItems((prev) => [msg.payload, ...prev]);
            setUnreadCount((c) => c + 1);
          }
        } catch (e) {
          // ignore
        }
      };

      ws.onerror = () => {
        startPolling();
      };

      ws.onclose = () => {
        wsRef.current = null;
        startPolling();

        const attempt = Math.min(6, reconnectAttemptRef.current + 1);
        reconnectAttemptRef.current = attempt;
        const delayMs = Math.min(30000, 1000 * (2 ** (attempt - 1)));

        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          connectWs();
        }, delayMs);
      };
    };

    connectWs();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      stopPolling();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isAuthenticated, refresh]);

  const value = useMemo(() => ({
    items,
    unreadCount,
    refresh,
    refreshUnreadCount,
    markRead,
    markAllRead,
  }), [items, unreadCount, refresh, refreshUnreadCount, markRead, markAllRead]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
};

export default NotificationContext;
